"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
var locale_1 = require("./locale");
var LookupList = /** @class */ (function () {
    function LookupList() {
        /** Data object where the properties are quality (in string format) and their values a set containing locale
         * identifiers using the `language`-`country` format and ISO 639-1 alpha-2 language code. */
        this.localesAndLanguagesByQuality = {};
        /** Data object where the properties are quality (in string format) and their value a set of ISO 639-1 alpha-2
         * language code. */
        this.unsupportedLocaleLanguagesByQuality = {};
    }
    /**
     * Add a locale in the data object matching its quality.
     *
     * @param quality - The HTTP quality factor associated with a locale.
     * @param identifier - A locale identifier using the `language`-`country` format.
     */
    LookupList.prototype.addLocale = function (quality, identifier) {
        if (!locale_1.default.isLocale(identifier)) {
            throw new Error("invalid locale identifier '" + identifier + "'");
        }
        if (!this.localesAndLanguagesByQuality[quality]) {
            this.localesAndLanguagesByQuality[quality] = new Set();
        }
        this.localesAndLanguagesByQuality[quality].add(identifier);
    };
    /**
     * Add a language in the data object matching its quality.
     *
     * @param quality - The HTTP quality factor associated with a language.
     * @param languageCode - An ISO 639-1 alpha-2 language code.
     */
    LookupList.prototype.addLanguage = function (quality, languageCode) {
        if (!locale_1.default.isLanguageCode(languageCode)) {
            throw new Error("invalid ISO 639-1 alpha-2 language code '" + languageCode + "'");
        }
        if (!this.localesAndLanguagesByQuality[quality]) {
            this.localesAndLanguagesByQuality[quality] = new Set();
        }
        this.localesAndLanguagesByQuality[quality].add(languageCode);
    };
    /**
     * Add an unsupported locale's language in the data object matching its quality.
     *
     * @param quality - The HTTP quality factor associated with an unsupported locale's language.
     * @param languageCode - An ISO 639-1 alpha-2 language code.
     */
    LookupList.prototype.addUnsupportedLocaleLanguage = function (quality, languageCode) {
        if (!locale_1.default.isLanguageCode(languageCode)) {
            throw new Error("invalid ISO 639-1 alpha-2 language code '" + languageCode + "'");
        }
        if (!this.unsupportedLocaleLanguagesByQuality[quality]) {
            this.unsupportedLocaleLanguagesByQuality[quality] = new Set();
        }
        this.unsupportedLocaleLanguagesByQuality[quality].add(languageCode);
    };
    /**
     * Get the best locale match from the lookup list.
     *
     * @param localeList - The list of locale from which the top language can be selected.
     * @param defaultLocale - The default locale object when no match is found.
     *
     * @returns The best match when found, otherwise the default locale identifier.
     */
    LookupList.prototype.getBestMatch = function (localeList, defaultLocale) {
        var _a, _b;
        var bestMatch;
        // Check if there is any matching locale identifiers or language code.
        if (Object.entries(this.localesAndLanguagesByQuality).length) {
            var localeOrLanguage_1 = Object.entries(this.localesAndLanguagesByQuality)
                .sort()
                .reverse()[0][1]
                .values()
                .next().value;
            if (locale_1.default.isLocale(localeOrLanguage_1)) {
                bestMatch = localeOrLanguage_1;
            }
            else {
                // The value is a language code.
                if (localeOrLanguage_1 !== defaultLocale.languageCode) {
                    // Only search for a match if the language does not match the default locale's.
                    bestMatch = (_a = localeList.objects.find(function (_a) {
                        var languageCode = _a.languageCode;
                        return languageCode === localeOrLanguage_1;
                    })) === null || _a === void 0 ? void 0 : _a.identifier;
                }
            }
        }
        else if (Object.entries(this.unsupportedLocaleLanguagesByQuality).length) {
            // Before using the default locale, check if one of the unsupported locale's language can be found.
            var unsupportedLocaleLanguage_1 = Object.entries(this.unsupportedLocaleLanguagesByQuality)
                .sort()
                .reverse()[0][1]
                .values()
                .next().value;
            if (unsupportedLocaleLanguage_1 !== defaultLocale.languageCode) {
                // Only search for a match if the language does not match the default locale's.
                bestMatch = (_b = localeList.objects.find(function (_a) {
                    var languageCode = _a.languageCode;
                    return languageCode === unsupportedLocaleLanguage_1;
                })) === null || _b === void 0 ? void 0 : _b.identifier;
            }
        }
        // Return the best match or the default locale.
        return bestMatch ? bestMatch : defaultLocale.identifier;
    };
    return LookupList;
}());
exports.default = LookupList;
